import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { ExcursionModel } from "../../api/excursions/ExcursionModel";
import { ExcursionAdapter } from "../../api/excursions/ExcursionAdapter";
import type {
  Excursion,
  ExcursionRequest,
  ExcursionParams,
  ExcursionParamsInput,
  ExcursionGroupParams,
  ExcursionGroupParamsInput,
  ExcursionBooking,
  ExcursionBookingFilters,
  BookingStatus,
  CreateExcursionBookingRequest,
} from "../../api/excursions/Excursion.typed";
import type { MultiComment } from "../../api/zekay/comments";
import { HttpError } from "../../api/zekay/httpClient";
import type {
  ExcursionSMSMessagesResponse,
  SendExcursionSMSOutcome,
  SendExcursionSMSRequest,
} from "../../api/smsTemplates/SMSTemplate.typed";

interface ExcursionContextType {
  listExcursions: (from: string, to: string) => Promise<Excursion[] | undefined>;
  getExcursion: (id: number) => Promise<Excursion | undefined>;
  getExcursionsBatch: (ids: number[]) => Promise<Excursion[] | undefined>;
  listExcursionParams: () => Promise<ExcursionParams[] | undefined>;
  getExcursionParams: () => Promise<ExcursionParams | null | undefined>;
  getExcursionParamsById: (id: number) => Promise<ExcursionParams | null | undefined>;
  createExcursionParams: (payload: ExcursionParamsInput) => Promise<ExcursionParams | undefined>;
  updateExcursionParams: (
    id: number,
    payload: ExcursionParamsInput,
  ) => Promise<ExcursionParams | undefined>;
  deleteExcursionParams: (id: number) => Promise<void | undefined>;
  listExcursionGroupParams: () => Promise<ExcursionGroupParams[] | undefined>;
  getExcursionGroupParams: () => Promise<ExcursionGroupParams | null | undefined>;
  getExcursionGroupParamsById: (id: number) => Promise<ExcursionGroupParams | null | undefined>;
  createExcursionGroupParams: (
    payload: ExcursionGroupParamsInput,
  ) => Promise<ExcursionGroupParams | undefined>;
  updateExcursionGroupParams: (
    id: number,
    payload: ExcursionGroupParamsInput,
  ) => Promise<ExcursionGroupParams | undefined>;
  deleteExcursionGroupParams: (id: number) => Promise<void | undefined>;
  createExcursion: (payload: ExcursionRequest) => Promise<Excursion | undefined>;
  updateExcursion: (id: number, payload: ExcursionRequest) => Promise<Excursion | undefined>;
  deleteExcursion: (id: number) => Promise<void>;
  updateExcursionGroupComments: (
    excursionId: number,
    groupId: number,
    comments: MultiComment[],
  ) => Promise<void | undefined>;
  listExcursionBookings: (
    filters?: ExcursionBookingFilters,
  ) => Promise<ExcursionBooking[] | undefined>;
  getExcursionBooking: (id: number) => Promise<ExcursionBooking | undefined>;
  createExcursionBooking: (
    payload: CreateExcursionBookingRequest,
  ) => Promise<ExcursionBooking | undefined>;
  updateExcursionBookingStatus: (
    id: number,
    status: BookingStatus,
  ) => Promise<ExcursionBooking | undefined>;
  bulkUpdateExcursionBookingStatus: (
    ids: number[],
    status: "validated" | "rejected",
  ) => Promise<{ succeeded: number; failed: number }>;
  deleteExcursionBooking: (id: number) => Promise<void | undefined>;
  sendExcursionSMS: (
    id: number,
    payload: SendExcursionSMSRequest,
  ) => Promise<SendExcursionSMSOutcome>;
  listExcursionSMSMessages: (id: number) => Promise<ExcursionSMSMessagesResponse | undefined>;
}

const ExcursionContext = React.createContext<ExcursionContextType>(null!);

export function ExcursionProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    const model = new ExcursionModel(appConfig);
    return new ExcursionAdapter(model);
  }, [appConfig]);

  const wrap = React.useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      try {
        return await fn();
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return undefined;
      }
    },
    [showAlert],
  );

  const listExcursions = React.useCallback(
    async (from: string, to: string): Promise<Excursion[] | undefined> =>
      adapter ? wrap(() => adapter.listExcursions(from, to)) : undefined,
    [adapter, wrap],
  );

  const getExcursion = React.useCallback(
    async (id: number): Promise<Excursion | undefined> =>
      adapter ? wrap(() => adapter.getExcursion(id)) : undefined,
    [adapter, wrap],
  );

  const getExcursionsBatch = React.useCallback(
    async (ids: number[]): Promise<Excursion[] | undefined> =>
      adapter ? wrap(() => adapter.getExcursionsBatch(ids)) : undefined,
    [adapter, wrap],
  );

  const listExcursionParams = React.useCallback(
    async (): Promise<ExcursionParams[] | undefined> =>
      adapter ? wrap(() => adapter.listExcursionParams()) : undefined,
    [adapter, wrap],
  );

  const getExcursionParams = React.useCallback(
    async (): Promise<ExcursionParams | null | undefined> =>
      adapter ? wrap(() => adapter.getExcursionParams()) : undefined,
    [adapter, wrap],
  );

  const getExcursionParamsById = React.useCallback(
    async (id: number): Promise<ExcursionParams | null | undefined> =>
      adapter ? wrap(() => adapter.getExcursionParamsById(id)) : undefined,
    [adapter, wrap],
  );

  const createExcursionParams = React.useCallback(
    async (payload: ExcursionParamsInput): Promise<ExcursionParams | undefined> => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.createExcursionParams(payload));
      if (result !== undefined) showAlert("Template de paramètres créé", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const updateExcursionParams = React.useCallback(
    async (id: number, payload: ExcursionParamsInput): Promise<ExcursionParams | undefined> => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.updateExcursionParams(id, payload));
      if (result !== undefined) showAlert("Paramètres des sorties enregistrés", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const deleteExcursionParams = React.useCallback(
    async (id: number): Promise<void | undefined> => {
      if (!adapter) return undefined;
      await wrap(() => adapter.deleteExcursionParams(id));
      showAlert("Template de paramètres supprimé", "success");
    },
    [adapter, wrap, showAlert],
  );

  const listExcursionGroupParams = React.useCallback(
    async (): Promise<ExcursionGroupParams[] | undefined> =>
      adapter ? wrap(() => adapter.listExcursionGroupParams()) : undefined,
    [adapter, wrap],
  );

  const getExcursionGroupParams = React.useCallback(
    async (): Promise<ExcursionGroupParams | null | undefined> =>
      adapter ? wrap(() => adapter.getExcursionGroupParams()) : undefined,
    [adapter, wrap],
  );

  const getExcursionGroupParamsById = React.useCallback(
    async (id: number): Promise<ExcursionGroupParams | null | undefined> =>
      adapter ? wrap(() => adapter.getExcursionGroupParamsById(id)) : undefined,
    [adapter, wrap],
  );

  const createExcursionGroupParams = React.useCallback(
    async (payload: ExcursionGroupParamsInput): Promise<ExcursionGroupParams | undefined> => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.createExcursionGroupParams(payload));
      if (result !== undefined) showAlert("Template de paramètres groupe créé", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const updateExcursionGroupParams = React.useCallback(
    async (
      id: number,
      payload: ExcursionGroupParamsInput,
    ): Promise<ExcursionGroupParams | undefined> => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.updateExcursionGroupParams(id, payload));
      if (result !== undefined) showAlert("Paramètres des groupes enregistrés", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const deleteExcursionGroupParams = React.useCallback(
    async (id: number): Promise<void | undefined> => {
      if (!adapter) return undefined;
      await wrap(() => adapter.deleteExcursionGroupParams(id));
      showAlert("Template de paramètres groupe supprimé", "success");
    },
    [adapter, wrap, showAlert],
  );

  const createExcursion = React.useCallback(
    async (payload: ExcursionRequest): Promise<Excursion | undefined> => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.createExcursion(payload));
      if (result !== undefined) showAlert("Sortie créée", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const updateExcursion = React.useCallback(
    async (id: number, payload: ExcursionRequest): Promise<Excursion | undefined> => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.updateExcursion(id, payload));
      if (result !== undefined) showAlert("Sortie mise à jour", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const deleteExcursion = React.useCallback(
    async (id: number): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.deleteExcursion(id);
        showAlert("Sortie supprimée", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const updateExcursionGroupComments = React.useCallback(
    async (
      excursionId: number,
      groupId: number,
      comments: MultiComment[],
    ): Promise<void | undefined> => {
      if (!adapter) return undefined;
      return wrap(() => adapter.updateExcursionGroupComments(excursionId, groupId, comments));
    },
    [adapter, wrap],
  );

  const listExcursionBookings = React.useCallback(
    async (filters?: ExcursionBookingFilters): Promise<ExcursionBooking[] | undefined> => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.listExcursionBookings(filters));
      return result ?? [];
    },
    [adapter, wrap],
  );

  const getExcursionBooking = React.useCallback(
    async (id: number): Promise<ExcursionBooking | undefined> =>
      adapter ? wrap(() => adapter.getExcursionBooking(id)) : undefined,
    [adapter, wrap],
  );

  const createExcursionBooking = React.useCallback(
    async (payload: CreateExcursionBookingRequest): Promise<ExcursionBooking | undefined> => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.createExcursionBooking(payload));
      if (result !== undefined) showAlert("Prestation créée", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const updateExcursionBookingStatus = React.useCallback(
    async (id: number, status: BookingStatus): Promise<ExcursionBooking | undefined> => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.updateExcursionBooking(id, { status }));
      if (result !== undefined) {
        const label = status === "validated" ? "validée" : "refusée";
        showAlert(`Prestation ${label}`, "success");
      }
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const bulkUpdateExcursionBookingStatus = React.useCallback(
    async (
      ids: number[],
      status: "validated" | "rejected",
    ): Promise<{ succeeded: number; failed: number }> => {
      if (!adapter || ids.length === 0) {
        return { succeeded: 0, failed: ids.length };
      }

      let succeeded = 0;
      let failed = 0;
      let lastMessage: string | undefined;

      for (const id of ids) {
        try {
          await adapter.updateExcursionBooking(id, { status });
          succeeded++;
        } catch (err) {
          failed++;
          lastMessage = err instanceof Error ? err.message : undefined;
        }
      }

      if (succeeded > 0 && failed === 0) {
        const plural = succeeded > 1;
        const noun = plural ? "prestations" : "prestation";
        const past =
          status === "validated"
            ? plural
              ? "validées"
              : "validée"
            : plural
              ? "refusées"
              : "refusée";
        showAlert(`${succeeded} ${noun} ${past}`, "success");
      } else if (succeeded > 0 && failed > 0) {
        showAlert(`${succeeded} mise(s) à jour, ${failed} erreur(s).`, "warning");
      } else if (failed > 0) {
        showAlert(lastMessage ?? "Mise à jour impossible", "danger");
      }

      return { succeeded, failed };
    },
    [adapter, showAlert],
  );

  const deleteExcursionBooking = React.useCallback(
    async (id: number): Promise<void | undefined> => {
      if (!adapter) return undefined;
      await wrap(() => adapter.deleteExcursionBooking(id));
      showAlert("Prestation supprimée", "success");
    },
    [adapter, wrap, showAlert],
  );

  // No toast: callers render per-recipient outcomes themselves.
  const sendExcursionSMS = React.useCallback(
    async (id: number, payload: SendExcursionSMSRequest): Promise<SendExcursionSMSOutcome> => {
      if (!adapter) return { ok: false, error: "Configuration indisponible" };
      try {
        return { ok: true, result: await adapter.sendExcursionSMS(id, payload) };
      } catch (err) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Erreur",
          code: err instanceof HttpError ? err.code : undefined,
        };
      }
    },
    [adapter],
  );

  const listExcursionSMSMessages = React.useCallback(
    async (id: number): Promise<ExcursionSMSMessagesResponse | undefined> =>
      adapter ? wrap(() => adapter.listExcursionSMSMessages(id)) : undefined,
    [adapter, wrap],
  );

  const value = React.useMemo<ExcursionContextType>(
    () => ({
      listExcursions,
      getExcursion,
      getExcursionsBatch,
      listExcursionParams,
      getExcursionParams,
      getExcursionParamsById,
      createExcursionParams,
      updateExcursionParams,
      deleteExcursionParams,
      listExcursionGroupParams,
      getExcursionGroupParams,
      getExcursionGroupParamsById,
      createExcursionGroupParams,
      updateExcursionGroupParams,
      deleteExcursionGroupParams,
      createExcursion,
      updateExcursion,
      deleteExcursion,
      updateExcursionGroupComments,
      listExcursionBookings,
      getExcursionBooking,
      createExcursionBooking,
      updateExcursionBookingStatus,
      bulkUpdateExcursionBookingStatus,
      deleteExcursionBooking,
      sendExcursionSMS,
      listExcursionSMSMessages,
    }),
    [
      listExcursions,
      getExcursion,
      getExcursionsBatch,
      listExcursionParams,
      getExcursionParams,
      getExcursionParamsById,
      createExcursionParams,
      updateExcursionParams,
      deleteExcursionParams,
      listExcursionGroupParams,
      getExcursionGroupParams,
      getExcursionGroupParamsById,
      createExcursionGroupParams,
      updateExcursionGroupParams,
      deleteExcursionGroupParams,
      createExcursion,
      updateExcursion,
      deleteExcursion,
      updateExcursionGroupComments,
      listExcursionBookings,
      getExcursionBooking,
      createExcursionBooking,
      updateExcursionBookingStatus,
      bulkUpdateExcursionBookingStatus,
      deleteExcursionBooking,
      sendExcursionSMS,
      listExcursionSMSMessages,
    ],
  );

  return <ExcursionContext.Provider value={value}>{props.children}</ExcursionContext.Provider>;
}

export function useExcursion(): ExcursionContextType {
  const ctx = React.useContext(ExcursionContext);
  if (!ctx) throw new Error("useExcursion must be used within ExcursionProvider");
  return ctx;
}
